/**
 * Step 4b — player controllers (owner: controller/movement team). The PlayerState machine for both players
 * (ARCHITECTURE §5 "Player controller" + "Controls"): hop-run, dash (double-tap), stutter (opposite direction in
 * dash F2–12), hesitation (6–24 f stop then a direction), jump / hurdle, fake-jump slam cancel (▼ while rising
 * F7–22: 3 f freeze → 2 f slam → 8 f stumble), fast-fall, truck (▼ hold: crouch → burst), spin (▼+Action
 * chord, i-frames), stiff-arm (Fist), dive / fake dive, leap grab, throw charge → release (THROW_CURVE) / pump
 * fake, catch secure, stun, tackled, celebrate, hard-count flinch, special pose. Stamina gate (STAMINA rule),
 * feint / commitment bookkeeping (bait rules), glove pose + target.
 *
 * Writes velocities (and the throw); positions are integrated by physics/integrate.ts. Frame counting follows
 * player/fsm.ts: `stateFrame` is incremented first, then the state's handler runs; a handler that enters a new
 * state with `enterNow` has the new state's handler run on the same tick (frame 1), so every move starts on the
 * tick its button fires (Head Soccer snappiness). Nothing here allocates except events.
 */
import {
  DT,
  FRAMES,
  HITBOX,
  MOTION,
  MOVE,
  PHYSICS,
  PUMP_FAKES_PER_PLAY,
  RULES,
  SPECIALS_TUNING,
  STAMINA,
  SpecialId,
} from '@hf/shared';
import { launchBall } from '../ball/launch';
import { beginFxTick, commitFailed, recordCommit, recordFeint, resolveCommitBaits } from '../contact/fx';
import {
  BallState,
  BtnIndex,
  CommitKind,
  FeintKind,
  Phase,
  PlayCall,
  PlayerState,
  SpecialPhase,
  isKickCall,
} from '../enums';
import { kickoffPending } from '../selectors';
import { consumeDoubleJump } from '../specials/update';
import type { SimEventSink } from '../sink';
import type { InputHistory, PlayerSim, SimState } from '../state';
import {
  Role,
  airControl,
  chordCancelReady,
  chordReady,
  dirOf,
  doubleTapReady,
  groundControl,
  holdsBall,
  opponentOf,
  pursuing,
  roleOf,
  loadSpeeds,
  shuffleControl,
  spd,
  settle,
} from './ctx';
import {
  BACKSTEP_TOTAL,
  DASH_TOTAL,
  DIVE_TOTAL,
  STIFF_ARM_TOTAL,
  THROW_RELEASE_TOTAL,
  TRUCK_BURST_TOTAL,
  truckChargeFrac,
} from './frames';
import { PF, PF_MASK, enterNow, lungeOpen, takeTransition, toNeutral } from './fsm';
import { updateGloveTarget } from './glove';
import { consumePress, hasBufferedPress, isHeld, keepPress } from './input';
import { regenStamina, trySpend } from './stamina';

/** stillFrames value meaning "this stop cannot become a hesitation" (it did not start at speed / too long). */
const HES_VOID = MOVE.hesitationMaxStopFrames + 1;
/** Handler re-runs per tick after `enterNow` (a guard against transition loops). */
const MAX_TRANSITIONS = 4;
/** PlayerSim.flags bit (≥ 16, owned by the specials team): Double Jump's second hop was already taken this airtime. */
const DOUBLE_JUMP_USED = 1 << 16;

// ── Small emit helpers (objects are built only when a sink exists) ─────────────────────────────────────

type AtEventType =
  | 'DASH'
  | 'FAST_FALL'
  | 'FAKE_CANCEL'
  | 'STUTTER'
  | 'HESITATION'
  | 'SPIN'
  | 'STUMBLE'
  | 'TRUCK_START'
  | 'DIVE_START'
  | 'FAKE_DIVE'
  | 'LEAP_START'
  | 'THROW_CHARGE'
  | 'PUMP_FAKE';

const emitAt = (s: SimState, ev: SimEventSink, type: AtEventType, p: PlayerSim): void => {
  if (!ev) return;
  if (type === 'DASH') ev.push({ type, tick: s.tick, actor: p.index, x: p.x, y: p.y, dir: p.dashDir });
  else ev.push({ type, tick: s.tick, actor: p.index, x: p.x, y: p.y });
};

/**
 * A lunge (DIVE / LEAP_GRAB) ended without connecting. Against a carrier it is a WHIFF (and may complete a
 * carrier's bait: fake jump / stutter / hesitation). Exported for the integrator (a leap that lands early).
 */
export const lungeWhiffed = (s: SimState, ev: SimEventSink, p: PlayerSim, kind: 'dive' | 'leap'): void => {
  if (!lungeOpen(p)) return;
  p.flags |= PF.LUNGE_DONE | PF.LUNGE_WHIFF;
  if ((p.flags & PF.VS_CARRIER) === 0) return;
  if (ev) ev.push({ type: 'WHIFF', tick: s.tick, actor: p.index, x: p.x, y: p.y, kind });
  commitFailed(s, ev, p);
};

// ── Move starters ──────────────────────────────────────────────────────────────────────────────────────

const startJump = (s: SimState, ev: SimEventSink, p: PlayerSim): void => {
  const hurdle = holdsBall(s, p);
  enterNow(p, PlayerState.AIRBORNE);
  p.vy = MOVE.jumpVelocity * p.stats.jumpMul;
  p.grounded = false;
  p.airFrame = 1;
  p.hurdling = hurdle;
  p.flags &= ~(PF.FAST_FALL | PF.FAST_FALL_USED | DOUBLE_JUMP_USED);
  if (ev) ev.push({ type: 'JUMP', tick: s.tick, actor: p.index, x: p.x, y: p.y, hurdle });
  recordCommit(s, ev, p, hurdle ? CommitKind.HURDLE : CommitKind.JUMP);
};

const startDash = (s: SimState, ev: SimEventSink, p: PlayerSim, dir: number): boolean => {
  if (!trySpend(s, ev, p, STAMINA.dash)) return false;
  enterNow(p, PlayerState.DASH);
  p.dashDir = dir;
  p.facing = dir;
  p.vx = dir * spd.dash;
  emitAt(s, ev, 'DASH', p);
  return true;
};

const startSpin = (s: SimState, ev: SimEventSink, p: PlayerSim): boolean => {
  consumePress(p.input, BtnIndex.D);
  consumePress(p.input, BtnIndex.A);
  if (!trySpend(s, ev, p, STAMINA.spin)) return false;
  const dir = dirOf(p.input);
  enterNow(p, PlayerState.SPIN);
  if (dir !== 0) p.facing = dir;
  p.spinDodged = false;
  p.iFrames = 0;
  emitAt(s, ev, 'SPIN', p);
  recordFeint(s, p, FeintKind.SPIN);
  recordCommit(s, ev, p, CommitKind.SPIN);
  return true;
};

const startLunge = (s: SimState, p: PlayerSim, st: PlayerState): void => {
  const dir = dirOf(p.input);
  enterNow(p, st);
  if (dir !== 0) p.facing = dir;
  p.flags &= ~(PF.LUNGE_DONE | PF.LUNGE_WHIFF | PF.VS_CARRIER);
  const b = s.ball;
  if (b.state === BallState.HELD && b.holder === opponentOf(s, p).index) p.flags |= PF.VS_CARRIER;
};

const startFastFall = (s: SimState, ev: SimEventSink, p: PlayerSim, role: Role): void => {
  if ((p.flags & PF.FAST_FALL_USED) !== 0) return;
  p.flags |= PF.FAST_FALL | PF.FAST_FALL_USED;
  if (p.vy > -PHYSICS.fastFallSpeed) p.vy = -PHYSICS.fastFallSpeed;
  emitAt(s, ev, 'FAST_FALL', p);
  if (role === Role.DEFENDER && s.ball.state === BallState.AIR && p.index !== s.rules.offense)
    recordFeint(s, p, FeintKind.FAST_FALL);
};

const startTruck = (s: SimState, ev: SimEventSink, p: PlayerSim): void => {
  consumePress(p.input, BtnIndex.D);
  enterNow(p, PlayerState.TRUCK_CHARGE);
  p.truckCharge = 0;
  emitAt(s, ev, 'TRUCK_START', p);
  recordCommit(s, ev, p, CommitKind.TRUCK);
};

// ── Ground actions shared by IDLE / RUN / DASH / LANDING-exit ──────────────────────────────────────────

/** ▼ held for the truck: a fresh press, or a hold that began after the snap (fake jump → slam → truck under). */
const truckRequested = (s: SimState, p: PlayerSim): boolean => {
  const h = p.input;
  if (h.lastPress[BtnIndex.D]! <= s.rules.snapTick) return false; // ▼ from the pre-snap FG formation
  return hasBufferedPress(h, BtnIndex.D) || isHeld(h, BtnIndex.D);
};

/**
 * Toss formations (FG_TOSS / PAT_TOSS / PUNT): the Action press that snapped the ball is still held
 * RULES.tossSnapHoldFrames later (never released since) and nothing has been thrown — the hold goes on into the
 * toss charge, like a kickoff's. A quick tap only snaps.
 */
const tossHeldThroughSnap = (s: SimState, p: PlayerSim): boolean => {
  const r = s.rules;
  const c = r.playCall;
  if (c !== PlayCall.FG_TOSS && c !== PlayCall.PAT_TOSS && c !== PlayCall.PUNT) return false;
  if (r.passThrown || (r.phase !== Phase.RUSH_COUNT && r.phase !== Phase.LIVE)) return false;
  const h = p.input;
  return h.held[BtnIndex.A]! >= RULES.tossSnapHoldFrames && h.lastPress[BtnIndex.A]! <= r.snapTick;
};

/**
 * Try every grounded action in priority order (spin chord, jump, Action, truck, dash). Returns true if one
 * started (the state changed). `allowDash` is false while already dashing.
 */
const groundActions = (
  s: SimState,
  ev: SimEventSink,
  p: PlayerSim,
  role: Role,
  allowDash: boolean,
): boolean => {
  const h = p.input;
  const ball = role === Role.QB || role === Role.CARRIER;
  // The kickoff kicker only moves (behind the LOS) and charges: ▼ is his onside call at the release.
  const kicker = role === Role.QB && kickoffPending(s);
  if (ball && !kicker && chordReady(h) && startSpin(s, ev, p)) return true;
  if (consumePress(h, BtnIndex.U)) {
    startJump(s, ev, p);
    return true;
  }
  if (hasBufferedPress(h, BtnIndex.A)) {
    consumePress(h, BtnIndex.A);
    if (role === Role.QB) enterNow(p, PlayerState.CHARGING_THROW);
    else if (role === Role.CARRIER) enterNow(p, PlayerState.STIFF_ARM);
    else {
      startLunge(s, p, PlayerState.DIVE);
      emitAt(s, ev, 'DIVE_START', p);
    }
    return true;
  }
  if (role === Role.QB && tossHeldThroughSnap(s, p)) {
    // FG / PAT / punt formation: the snap press held on = the toss charge, counted from the press.
    enterNow(p, PlayerState.CHARGING_THROW);
    p.stateFrame = h.held[BtnIndex.A]!;
    p.throwCharge = 0;
    emitAt(s, ev, 'THROW_CHARGE', p);
    return true;
  }
  if (ball && !kicker && truckRequested(s, p)) {
    startTruck(s, ev, p);
    return true;
  }
  if (allowDash) {
    if (doubleTapReady(h, BtnIndex.L)) {
      consumePress(h, BtnIndex.L);
      if (startDash(s, ev, p, -1)) return true;
    } else if (doubleTapReady(h, BtnIndex.R)) {
      consumePress(h, BtnIndex.R);
      if (startDash(s, ev, p, 1)) return true;
    }
  }
  return false;
};

/** IDLE ↔ RUN by speed/input without a transition (the run cycle keeps its frame count). */
const setGroundState = (p: PlayerSim, moving: boolean): void => {
  const st = moving ? PlayerState.RUN : PlayerState.IDLE;
  if (p.state !== st) {
    p.prevState = p.state;
    p.state = st;
    p.stateFrame = 1;
  }
};

// ── State handlers ─────────────────────────────────────────────────────────────────────────────────────

const groundNeutral = (s: SimState, ev: SimEventSink, p: PlayerSim, role: Role): void => {
  const h = p.input;
  const dir = dirOf(h);
  if (role === Role.INACTIVE) {
    groundControl(p, 0, 1);
    setGroundState(p, p.vx !== 0);
    return;
  }
  if (role === Role.PRE_DEFENSE && s.rules.playCall === PlayCall.KICKOFF) {
    // Kickoff receiver: free to run (turn, dash) behind the restraining line while he waits for the kick.
    if (doubleTapReady(h, BtnIndex.L)) {
      consumePress(h, BtnIndex.L);
      if (startDash(s, ev, p, -1)) return;
    } else if (doubleTapReady(h, BtnIndex.R)) {
      consumePress(h, BtnIndex.R);
      if (startDash(s, ev, p, 1)) return;
    }
    groundControl(p, dir, 1);
    if (dir !== 0) p.facing = dir;
    setGroundState(p, dir !== 0 || p.vx !== 0);
    return;
  }
  if (role === Role.PRE_OFFENSE || role === Role.PRE_DEFENSE) {
    // Pre-snap stance: shuffle without turning (the offense is clamped behind the LOS by the integrator).
    shuffleControl(p, dir);
    setGroundState(p, dir !== 0 || p.vx !== 0);
    return;
  }
  if (groundActions(s, ev, p, role, true)) return;

  const ball = role === Role.QB || role === Role.CARRIER;
  if (dir === 0) {
    if (p.stillFrames === 0)
      p.stillFrames =
        ball && (p.vx < 0 ? -p.vx : p.vx) >= MOTION.hesitationMinSpeedFrac * spd.run ? 1 : HES_VOID;
    else if (p.stillFrames < HES_VOID) p.stillFrames++;
  } else {
    if (
      ball &&
      p.stillFrames >= MOVE.hesitationMinStopFrames &&
      p.stillFrames <= MOVE.hesitationMaxStopFrames
    ) {
      p.burstFrames = MOVE.hesitationBurstFrames;
      loadSpeeds(s, p);
      p.vx = dir * spd.run;
      emitAt(s, ev, 'HESITATION', p);
      recordFeint(s, p, FeintKind.HESITATION);
    }
    p.stillFrames = 0;
    p.facing = dir;
  }
  groundControl(p, dir, 1);
  setGroundState(p, dir !== 0 || p.vx !== 0);
};

const dash = (s: SimState, ev: SimEventSink, p: PlayerSim, role: Role): void => {
  const f = p.stateFrame;
  const h = p.input;
  // (a kickoff receiver may dash while he waits for the kick: nothing else pre-snap)
  const kickoffReceiver = role === Role.PRE_DEFENSE && s.rules.playCall === PlayCall.KICKOFF;
  if (
    role === Role.INACTIVE ||
    role === Role.PRE_OFFENSE ||
    (role === Role.PRE_DEFENSE && !kickoffReceiver)
  ) {
    toNeutral(p);
    return;
  }
  if (f >= FRAMES.stutter.windowStart && f <= FRAMES.stutter.windowEnd) {
    const back = p.dashDir > 0 ? BtnIndex.L : BtnIndex.R;
    if (hasBufferedPress(h, back) && isHeld(h, back)) {
      consumePress(h, back);
      if (trySpend(s, ev, p, STAMINA.stutter)) {
        enterNow(p, PlayerState.BACKSTEP);
        return;
      }
    }
  }
  if (!kickoffReceiver && groundActions(s, ev, p, role, false)) return;
  if (f <= MOVE.dashFrames) {
    p.vx = p.dashDir * spd.dash;
    p.facing = p.dashDir;
  } else if (f <= DASH_TOTAL) {
    groundControl(p, dirOf(h), 1);
  } else toNeutral(p);
};

const backstep = (s: SimState, ev: SimEventSink, p: PlayerSim, role: Role): void => {
  const f = p.stateFrame;
  if (f === 1) {
    emitAt(s, ev, 'STUTTER', p);
    if (role === Role.QB || role === Role.CARRIER) recordFeint(s, p, FeintKind.STUTTER);
    else if (role === Role.DEFENDER) recordFeint(s, p, FeintKind.FAKE_RUSH);
  }
  p.facing = p.dashDir;
  if (f <= FRAMES.stutter.backstep) p.vx = -p.dashDir * MOVE.backstepSpeed * p.stats.speedMul;
  else if (f <= BACKSTEP_TOTAL) groundControl(p, 0, 1);
  else toNeutral(p);
};

const airborne = (s: SimState, ev: SimEventSink, p: PlayerSim, role: Role): void => {
  const h = p.input;
  const dir = dirOf(h);
  airControl(p, dir);
  if (dir !== 0) p.facing = dir;
  if (role === Role.INACTIVE || role === Role.PRE_OFFENSE || role === Role.PRE_DEFENSE) return;
  const ball = role === Role.QB || role === Role.CARRIER;
  if (
    hasBufferedPress(h, BtnIndex.U) &&
    p.special.id === SpecialId.DOUBLE_JUMP &&
    p.special.phase === SpecialPhase.ACTIVE &&
    (p.flags & DOUBLE_JUMP_USED) === 0
  ) {
    consumePress(h, BtnIndex.U);
    p.vy = MOVE.jumpVelocity * p.stats.jumpMul * SPECIALS_TUNING.doubleJump.jumpVelocityMul;
    p.flags |= DOUBLE_JUMP_USED;
    if (ev) ev.push({ type: 'JUMP', tick: s.tick, actor: p.index, x: p.x, y: p.y, hurdle: p.hurdling });
    consumeDoubleJump(s, ev, p);
  }
  if (hasBufferedPress(h, BtnIndex.D)) {
    const fc = FRAMES.fakeCancel;
    if (ball && p.vy > 0 && p.airFrame <= fc.windowEnd) {
      if (p.airFrame >= fc.windowStart) {
        consumePress(h, BtnIndex.D);
        if (trySpend(s, ev, p, STAMINA.fakeJumpCancel)) {
          enterNow(p, PlayerState.FAKE_FREEZE);
          return;
        }
      }
      // before the window: the press stays buffered into it (even one made on take-off, frame 1)
      else keepPress(h, BtnIndex.D);
    } else {
      consumePress(h, BtnIndex.D);
      startFastFall(s, ev, p, role);
    }
  }
  if (hasBufferedPress(h, BtnIndex.A)) {
    if (role === Role.QB) {
      consumePress(h, BtnIndex.A);
      enterNow(p, PlayerState.CHARGING_THROW);
    } else if (role === Role.DEFENDER) {
      consumePress(h, BtnIndex.A);
      startLunge(s, p, PlayerState.LEAP_GRAB);
    }
    // carrier: the press stays buffered for a stiff-arm on landing
  }
};

const fakeFreeze = (s: SimState, ev: SimEventSink, p: PlayerSim): void => {
  if (p.stateFrame === 1) {
    emitAt(s, ev, 'FAKE_CANCEL', p);
    recordFeint(s, p, FeintKind.FAKE_JUMP);
  }
  p.vx = 0;
  p.vy = 0;
  if (p.stateFrame > FRAMES.fakeCancel.freeze) enterNow(p, PlayerState.FAKE_SLAM);
};

const fakeSlam = (p: PlayerSim): void => {
  if (p.stateFrame === 1) {
    const fc = FRAMES.fakeCancel;
    const height = p.y - p.stats.headRadius;
    const exact = height > 0 ? height / (fc.slam * DT) : 0;
    p.vy = -(exact > fc.slamSpeed ? exact : fc.slamSpeed);
    p.vx = 0;
  }
  if (p.grounded) toNeutral(p); // safety: the integrator normally turns the landing into STUMBLE
};

/** LANDING / STUMBLE / STUNNED / TACKLED: `stunFrames` remaining; the player acts the frame after it runs out. */
const timedLock = (s: SimState, p: PlayerSim, role: Role): void => {
  if (p.stunFrames > 0) {
    p.stunFrames--;
    if (p.state === PlayerState.LANDING && (p.flags & PF.LUNGE_WHIFF) === 0 && role !== Role.INACTIVE) {
      groundControl(p, dirOf(p.input), 1);
      if (dirOf(p.input) !== 0) p.facing = dirOf(p.input);
    } else if (p.state === PlayerState.LANDING) groundControl(p, 0, 1);
    else settle(p);
    if (!p.grounded) airControl(p, 0);
    return;
  }
  p.flags &= ~PF.LUNGE_WHIFF;
  toNeutral(p);
};

const truckCharge = (s: SimState, ev: SimEventSink, p: PlayerSim, role: Role): void => {
  const T = FRAMES.truck;
  const f = p.stateFrame;
  const h = p.input;
  if (role !== Role.QB && role !== Role.CARRIER) {
    toNeutral(p);
    return;
  }
  // ▼ first: the crouch IS the chord's first half, so any fresh Action press while ▼ is still held is the SPIN
  // (a human's ▼…Action on a keyboard is often 4–6 ticks apart; MOVE.chordWindowFrames only bounds the
  // Action-first order, where the first press already started a stiff-arm / throw charge). startSpin consumes
  // both presses even when the stamina gate refuses it (the crouch then just goes on).
  const held = isHeld(h, BtnIndex.D);
  if (held && actionDuringCrouch(h) && startSpin(s, ev, p)) return;
  const dir = dirOf(h);
  if (dir !== 0) p.facing = dir;
  groundControl(p, dir, MOVE.truckCrawlMul);
  if (held) p.truckCharge = f > T.startup ? f - T.startup : 0;
  if (p.truckCharge >= T.maxCharge || (!held && p.truckCharge >= T.minCharge)) {
    enterNow(p, PlayerState.TRUCK_BURST);
    return;
  }
  if (!held) {
    // a mistimed chord (Action on the very tick ▼ came up) is dropped, never left buffered to fire as a throw
    if (actionDuringCrouch(h)) consumePress(h, BtnIndex.A);
    toNeutral(p);
  }
};

/** A buffered Action press made since the ▼ press that began this crouch. */
const actionDuringCrouch = (h: InputHistory): boolean =>
  hasBufferedPress(h, BtnIndex.A) && h.lastPress[BtnIndex.A]! >= h.lastPress[BtnIndex.D]!;

const truckBurst = (p: PlayerSim): void => {
  const f = p.stateFrame;
  if (f <= FRAMES.truck.burst) {
    const k = MOTION.truckBurstMinMul + (1 - MOTION.truckBurstMinMul) * truckChargeFrac(p);
    p.vx = p.facing * MOVE.truckBurstSpeed * p.stats.speedMul * k;
  } else if (f <= TRUCK_BURST_TOTAL) groundControl(p, 0, 1);
  else {
    p.truckCharge = 0;
    toNeutral(p);
  }
};

const spin = (s: SimState, p: PlayerSim): void => {
  const S = FRAMES.spin;
  const f = p.stateFrame;
  if (f === S.startup + 1) p.iFrames = S.active;
  if (f <= S.startup + S.active) {
    p.vx = p.facing * spd.run * MOTION.spinSpeedMul;
    return;
  }
  const rec = p.spinDodged ? S.recovery : S.whiffRecovery;
  // a spin that dodged flows out of it (steerable, at run speed); a whiffed spin plants
  if (f <= S.startup + S.active + rec) groundControl(p, p.spinDodged ? dirOf(p.input) : 0, 1);
  else toNeutral(p);
};

const stiffArm = (s: SimState, ev: SimEventSink, p: PlayerSim, role: Role): void => {
  const SA = FRAMES.stiffArm;
  const f = p.stateFrame;
  const h = p.input;
  if (f === 1) p.flags &= ~PF.STIFF_HIT;
  if (chordCancelReady(h, BtnIndex.A, BtnIndex.D)) {
    if (startSpin(s, ev, p)) return;
  }
  if (role === Role.INACTIVE) {
    toNeutral(p);
    return;
  }
  groundControl(p, dirOf(h), MOTION.stiffArmMoveMul);
  if (f === SA.startup + SA.active + 1 && (p.flags & PF.STIFF_HIT) === 0 && ev)
    ev.push({ type: 'STIFF_ARM', tick: s.tick, actor: p.index, x: p.x, y: p.y, hit: false });
  if (f > STIFF_ARM_TOTAL) toNeutral(p);
};

const dive = (s: SimState, ev: SimEventSink, p: PlayerSim, role: Role): void => {
  const D = FRAMES.dive;
  const f = p.stateFrame;
  if (f <= D.startup) {
    // Visible crouch wind-up. ▼ in F1..fakeWindowEnd cancels into a fake dive (if stamina allows).
    if (f <= D.fakeWindowEnd && role !== Role.INACTIVE && hasBufferedPress(p.input, BtnIndex.D)) {
      consumePress(p.input, BtnIndex.D);
      if (trySpend(s, ev, p, STAMINA.fakeDive)) {
        enterNow(p, PlayerState.FAKE_DIVE);
        return;
      }
    }
    // A running dive at a carrier fleeing from him (pursuit) keeps its momentum through the wind-up; any other
    // dive plants first.
    if (!(p.vx * p.facing > 0 && pursuitDive(s, p))) groundControl(p, 0, 1);
    else {
      // ...at most at his (pursuit) run speed: a dash into a dive does not launch a 17 yd/s missile
      if (p.vx * p.facing > spd.run) p.vx = p.facing * spd.run;
    }
    return;
  }
  if (f <= D.startup + D.active) {
    if (f === D.startup + 1) recordCommit(s, ev, p, CommitKind.DIVE);
    // the lunge itself is the same for everyone (a pursuer only kept his run speed through the wind-up)
    p.vx = p.facing * HITBOX.dive.lungeSpeed * p.stats.speedMul;
    return;
  }
  if (f === D.startup + D.active + 1) lungeWhiffed(s, ev, p, 'dive');
  p.vx = p.vx > 0 ? p.vx - MOTION.diveSlideDecel * DT : p.vx + MOTION.diveSlideDecel * DT;
  if ((p.vx > 0 ? p.vx : -p.vx) < MOTION.diveSlideDecel * DT) p.vx = 0;
  if (f > DIVE_TOTAL) toNeutral(p);
};

/** MOTION.pursuit.diveKeepsMomentum: a dive at the carrier from behind (pursuit) is a running dive. */
const pursuitDive = (s: SimState, p: PlayerSim): boolean =>
  MOTION.pursuit.diveKeepsMomentum && pursuing(s, p);

const fakeDive = (s: SimState, ev: SimEventSink, p: PlayerSim): void => {
  if (p.stateFrame === 1) {
    emitAt(s, ev, 'FAKE_DIVE', p);
    recordFeint(s, p, FeintKind.FAKE_DIVE);
  }
  groundControl(p, 0, 1);
  if (p.stateFrame > FRAMES.dive.fakeRecovery) toNeutral(p);
};

const leapGrab = (s: SimState, ev: SimEventSink, p: PlayerSim): void => {
  const L = FRAMES.leapGrab;
  const f = p.stateFrame;
  if (f === 1) {
    emitAt(s, ev, 'LEAP_START', p);
    recordCommit(s, ev, p, CommitKind.LEAP);
    const v = p.facing * p.vx; // speed along facing
    if (v < MOTION.leapLungeSpeed) p.vx = p.facing * MOTION.leapLungeSpeed;
  }
  if (f === L.startup + L.active + 1) lungeWhiffed(s, ev, p, 'leap');
  if (f > L.startup + L.active) airControl(p, dirOf(p.input));
  if (p.grounded) toNeutral(p); // landed on a platform-less frame (the integrator normally handles landings)
};

const chargingThrow = (s: SimState, ev: SimEventSink, p: PlayerSim, role: Role): void => {
  const f = p.stateFrame;
  const h = p.input;
  if (role !== Role.QB) {
    // Crossed the LOS / lost the ball / whistle: only QB mode can throw.
    p.throwCharge = 0;
    toNeutral(p);
    return;
  }
  if (f === 1) {
    p.throwCharge = 0;
    emitAt(s, ev, 'THROW_CHARGE', p);
  }
  p.facing = s.rules.attackDir;
  // A kick's charge has no spin cancel and no pump fake: ▼ held at the release is the kickoff's onside call.
  const kick = isKickCall(s.rules.playCall);
  if (!kick && chordCancelReady(h, BtnIndex.A, BtnIndex.D)) {
    if (startSpin(s, ev, p)) return;
  }
  // Pump fake: a ▼ press clearly after the chord window of the Action press that began this charge.
  if (
    !kick &&
    hasBufferedPress(h, BtnIndex.D) &&
    h.lastPress[BtnIndex.D]! - h.lastPress[BtnIndex.A]! > MOVE.chordWindowFrames
  ) {
    consumePress(h, BtnIndex.D);
    if (p.pumpFakesUsed < PUMP_FAKES_PER_PLAY && trySpend(s, ev, p, STAMINA.pumpFake)) {
      enterNow(p, PlayerState.PUMP_FAKE);
      return;
    }
  }
  if (!isHeld(h, BtnIndex.A)) {
    p.lob = isHeld(h, BtnIndex.U);
    enterNow(p, PlayerState.THROW_RELEASE);
    return;
  }
  p.throwCharge = f;
  if (p.grounded) groundControl(p, dirOf(h), MOTION.chargeMoveMul);
  else airControl(p, dirOf(h));
};

const throwRelease = (s: SimState, ev: SimEventSink, p: PlayerSim): void => {
  const f = p.stateFrame;
  if (f === 1) p.flags &= ~PF.THROWN;
  if (f === FRAMES.throwRelease.startup && (p.flags & PF.THROWN) === 0) {
    if (launchBall(s, ev, p)) p.flags |= PF.THROWN;
    else {
      toNeutral(p); // not in QB mode any more: no throw
      return;
    }
  }
  if (p.grounded) groundControl(p, dirOf(p.input), MOTION.throwMoveMul);
  else airControl(p, dirOf(p.input));
  if (f > THROW_RELEASE_TOTAL) {
    p.throwCharge = 0;
    toNeutral(p);
  }
};

const pumpFake = (s: SimState, ev: SimEventSink, p: PlayerSim, role: Role): void => {
  const f = p.stateFrame;
  if (f === 1) {
    p.pumpFakesUsed++;
    p.throwCharge = 0;
    emitAt(s, ev, 'PUMP_FAKE', p);
    recordFeint(s, p, FeintKind.PUMP_FAKE);
  }
  if (p.grounded) groundControl(p, dirOf(p.input), MOTION.throwMoveMul);
  else airControl(p, dirOf(p.input));
  if (f > FRAMES.pumpFake.recovery) {
    if (role === Role.QB && isHeld(p.input, BtnIndex.A)) enterNow(p, PlayerState.CHARGING_THROW);
    else toNeutral(p);
  }
};

const catchSecure = (p: PlayerSim): void => {
  if (p.grounded) settle(p);
  if (p.stateFrame > FRAMES.catch.secure) toNeutral(p);
};

const timedPose = (p: PlayerSim, frames: number): void => {
  settle(p);
  if (p.stateFrame > frames) toNeutral(p);
};

const specialPose = (p: PlayerSim): void => {
  settle(p);
  const tele = p.special.phase === SpecialPhase.TELEGRAPH;
  if (p.stateFrame > SPECIALS_TUNING.telegraphFrames || (!tele && p.stateFrame > 1)) toNeutral(p);
};

const runState = (s: SimState, ev: SimEventSink, p: PlayerSim, role: Role): void => {
  switch (p.state) {
    case PlayerState.IDLE:
    case PlayerState.RUN:
      if (!p.grounded) enterNow(p, PlayerState.AIRBORNE);
      else groundNeutral(s, ev, p, role);
      return;
    case PlayerState.DASH:
      dash(s, ev, p, role);
      return;
    case PlayerState.BACKSTEP:
      backstep(s, ev, p, role);
      return;
    case PlayerState.AIRBORNE:
      if (p.grounded) toNeutral(p);
      else airborne(s, ev, p, role);
      return;
    case PlayerState.LANDING:
    case PlayerState.STUMBLE:
    case PlayerState.STUNNED:
    case PlayerState.TACKLED:
      timedLock(s, p, role);
      return;
    case PlayerState.FAKE_FREEZE:
      fakeFreeze(s, ev, p);
      return;
    case PlayerState.FAKE_SLAM:
      fakeSlam(p);
      return;
    case PlayerState.TRUCK_CHARGE:
      truckCharge(s, ev, p, role);
      return;
    case PlayerState.TRUCK_BURST:
      truckBurst(p);
      return;
    case PlayerState.SPIN:
      spin(s, p);
      return;
    case PlayerState.STIFF_ARM:
      stiffArm(s, ev, p, role);
      return;
    case PlayerState.DIVE:
      dive(s, ev, p, role);
      return;
    case PlayerState.FAKE_DIVE:
      fakeDive(s, ev, p);
      return;
    case PlayerState.LEAP_GRAB:
      leapGrab(s, ev, p);
      return;
    case PlayerState.CHARGING_THROW:
      chargingThrow(s, ev, p, role);
      return;
    case PlayerState.THROW_RELEASE:
      throwRelease(s, ev, p);
      return;
    case PlayerState.PUMP_FAKE:
      pumpFake(s, ev, p, role);
      return;
    case PlayerState.CATCH:
      catchSecure(p);
      return;
    case PlayerState.CELEBRATE:
      // The scorer keeps celebrating through the dead ball (and after GAME_OVER) until the next line-up
      // (rules/helpers.ts protocol); a celebration during live play is a timed pose.
      if (role === Role.INACTIVE) settle(p);
      else timedPose(p, FRAMES.celebrate.frames);
      return;
    case PlayerState.HARD_COUNT:
      if (role !== Role.PRE_OFFENSE)
        toNeutral(p); // snapped mid-flinch: play on
      else timedPose(p, FRAMES.hardCount.recovery);
      return;
    case PlayerState.SPECIAL_POSE:
      specialPose(p);
      return;
    default:
      toNeutral(p);
  }
};

/**
 * Pass 2 for one player, once BOTH state machines ran (so nothing depends on the player index): a lunge started
 * this tick targets the carrier only if the opponent still holds the ball after his own move (a throw on the
 * same tick makes it a catch lunge), then the glove pose + target (a throw on this tick is tracked by either
 * player's glove at once).
 */
const finishPlayer = (s: SimState, p: PlayerSim): void => {
  if ((p.state === PlayerState.DIVE || p.state === PlayerState.LEAP_GRAB) && p.stateFrame === 1) {
    const b = s.ball;
    if (b.state === BallState.HELD && b.holder === opponentOf(s, p).index) p.flags |= PF.VS_CARRIER;
    else p.flags &= ~PF.VS_CARRIER;
  }
  updateGloveTarget(s, p);
};

/**
 * Both controllers, symmetric in the player index: pass 1 runs both state machines, pass 2 settles what depends
 * on the other player's move this tick (lunge target, glove tracking, commit-time baits such as a pump fake
 * against a jump on the same tick).
 */
export const updateControllers = (s: SimState, ev: SimEventSink): void => {
  beginFxTick();
  const ph = s.rules.phase;
  const live = ph === Phase.RUSH_COUNT || ph === Phase.LIVE;
  const preSnap = ph === Phase.PRE_SNAP;
  // pass 1: frame counters, then the state machine (with same-tick transitions)
  for (let i = 0; i < 2; i++) {
    const p = s.players[i as 0 | 1];
    if (preSnap) p.flags &= ~PF_MASK;
    p.stateFrame++;
    if (!p.grounded && p.airFrame > 0) p.airFrame++;
    if (p.iFrames > 0) p.iFrames--;
    if (p.burstFrames > 0) p.burstFrames--;
    if (live) regenStamina(p);
    takeTransition();
    loadSpeeds(s, p);
    runState(s, ev, p, roleOf(s, p));
    for (let k = 0; k < MAX_TRANSITIONS && takeTransition(); k++) {
      loadSpeeds(s, p);
      runState(s, ev, p, roleOf(s, p));
    }
    takeTransition();
  }
  // pass 2: lunge targets + glove targets for both, then the commit-time baits
  finishPlayer(s, s.players[0]);
  finishPlayer(s, s.players[1]);
  resolveCommitBaits(s, ev);
};
